import React, { useState, useEffect } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface IOSCompatibilityCheckProps {
    children: React.ReactNode;
}

/**
 * Component that checks iOS version compatibility and shows a blocking message
 * for unsupported iOS versions (< 16) that cannot play encrypted HLS videos.
 */
export const IOSCompatibilityCheck: React.FC<IOSCompatibilityCheckProps> = ({ children }) => {
    const { theme } = useTheme();
    const { l10n } = useLocale();
    const responsive = useResponsiveDesign();
    const [isCompatible, setIsCompatible] = useState(true);
    const [iOSVersion, setIOSVersion] = useState<number | null>(null);

    useEffect(() => {
        // Check if running on iOS Safari
        const userAgent = navigator.userAgent;
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

        if (!isIOSDevice) {
            // Not iOS, all good
            setIsCompatible(true);
            return;
        }

        // Extract iOS version from user agent
        // Format: "OS 15_1" or "OS 16_0"
        const iOSVersionMatch = userAgent.match(/OS (\d+)_/);
        const version = iOSVersionMatch ? parseInt(iOSVersionMatch[1]) : null;

        setIOSVersion(version);

        // iOS 16+ is required for encrypted HLS video playback in browser
        // iOS < 16 lacks MediaSource Extensions support
        if (version && version < 16) {
            setIsCompatible(false);

            // Log to console for debugging
            console.warn(`[Expause] iOS ${version} detected - Encrypted video playback not supported in browser`);
        } else {
            setIsCompatible(true);
        }
    }, []);

    // If not compatible, show blocking message
    if (!isCompatible) {
        return (
            <div
                {...qaAttr({ id: 'ios-compatibility-gate', status: 'blocked' })}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '24px',
                    backgroundColor: theme.background,
                    color: theme.text.primary,
                    textAlign: 'center'
                }}
            >
                <div
                    style={{
                        maxWidth: '500px',
                        backgroundColor: theme.surface,
                        padding: '24px',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    {/* Warning Icon */}
                    <div
                        style={{
                            width: '80px',
                            height: '80px',
                            margin: '0 auto 24px',
                            backgroundColor: theme.warning,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontSize: responsive.font['3xl'],
                            fontWeight: responsive.fontWeight.bold,
                            marginBottom: '16px',
                            color: theme.text.primary
                        }}
                    >
                        {l10n.iosCompatibility.title}
                    </h1>

                    {/* Message */}
                    <p
                        style={{
                            fontSize: responsive.font.base,
                            lineHeight: '1.6',
                            marginBottom: '24px',
                            color: theme.text.secondary
                        }}
                    >
                        {l10n.iosCompatibility.message.replace('{iOSVersion}', String(iOSVersion || 'an older version'))}
                    </p>

                    {/* Recommendations */}
                    <div
                        style={{
                            borderRadius: '12px',
                            marginBottom: '24px',
                            textAlign: 'left'
                        }}
                    >
                        <h3
                            style={{
                                fontSize: responsive.font.lg,
                                fontWeight: responsive.fontWeight.semibold,
                                marginBottom: '12px',
                                color: theme.text.primary
                            }}
                        >
                            {l10n.iosCompatibility.recommendationsTitle}
                        </h3>
                        <ul
                            style={{
                                fontSize: responsive.font.base,
                                lineHeight: '1.8',
                                color: theme.text.secondary,
                                paddingLeft: '16px',
                                margin: 0
                            }}
                        >
                            <li>{l10n.iosCompatibility.recommendations.updateIOS}</li>
                            <li>{l10n.iosCompatibility.recommendations.downloadApp}</li>
                            <li>{l10n.iosCompatibility.recommendations.useDifferentDevice}</li>
                        </ul>
                    </div>

                    {/* App Store Link */}
                    <a
                        {...qaAttr({ id: 'ios-compatibility-app-store' })}
                        href="https://apps.apple.com/us/app/expause/id6740270283"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block',
                            padding: '14px 32px',
                            border: `2px solid ${theme.exclusive}`,
                            color: theme.text.primary,
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontSize: responsive.font.base,
                            fontWeight: responsive.fontWeight.semibold,
                            transition: 'opacity 0.2s',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    >
                        {l10n.iosCompatibility.buttonText}
                    </a>
                </div>

                {/* Footer note */}
                <p
                    style={{
                        marginTop: '32px',
                        fontSize: responsive.font.sm,
                        color: theme.text.secondary,
                        opacity: 0.7,
                        maxWidth: '400px'
                    }}
                >
                    {l10n.iosCompatibility.footerNote}
                </p>
            </div>
        );
    }

    // Compatible - render children
    return <>{children}</>;
};
